import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Calendar, Play, Ticket, Music, Heart, Star, ChevronRight, Send, Clock, X, Info, CreditCard, Loader2, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  SHOW_INFO, CONTACT_INFO, REVIEWS, 
  GALLERY_IMAGES, VIDEO_PROMO, generateWhatsAppUrl, VENUES, getVenueForDate
} from '../data/showData';
import SeatMap from '../components/SeatMap';

const Home = () => {
  const location = useLocation();
  const [isSticky, setIsSticky] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isSubmittingWhatsApp, setIsSubmittingWhatsApp] = useState(false);
  const [capacityData, setCapacityData] = useState({});
  const [showDates, setShowDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [occupiedSeats, setOccupiedSeats] = useState([]);
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(false);
  const [dateFormData, setDateFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    numBiglietti: '1',
    numBigliettiRidotti: '0'
  });
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    telefono: '',
    numBiglietti: '1',
    data: ''
  });
  const [theatreForm, setTheatreForm] = useState({
    nomeCompleto: '',
    nomeTeatro: '',
    citta: '',
    email: '',
    telefono: '',
    capienza: '',
    periodo: ''
  });
  const [theatreErrors, setTheatreErrors] = useState({});

  // Gestisci hash nell'URL per scrollare alla sezione corretta
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.hash]);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch events/dates from API
  useEffect(() => {
    const fetchEvents = async () => {
      setLoadingDates(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events`);
        if (response.ok) {
          const data = await response.json();
          
          // Helper to parse Italian date
          const parseItalianDate = (dateStr) => {
            const months = {
              'Gennaio': 0, 'Febbraio': 1, 'Marzo': 2, 'Aprile': 3,
              'Maggio': 4, 'Giugno': 5, 'Luglio': 6, 'Agosto': 7,
              'Settembre': 8, 'Ottobre': 9, 'Novembre': 10, 'Dicembre': 11
            };
            const parts = dateStr.split(' ');
            if (parts.length >= 3) {
              const day = parseInt(parts[0]);
              const month = months[parts[1]];
              const year = parseInt(parts[2]);
              return new Date(year, month, day);
            }
            return new Date(9999, 11, 31);
          };
          
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          // Transform API data and filter out past dates
          const events = (data.events || [])
            .filter(event => {
              const eventDate = parseItalianDate(event.date);
              return eventDate >= today; // Solo date future o di oggi
            })
            .map(event => ({
              date: event.date,
              day: event.day,
              time: event.time,
              available: !event.sold_out && event.available !== false,
              venueId: event.venue_id,
              phone: event.phone,
              prices: event.prices,
              note: event.note
            }));
          setShowDates(events);
        }
      } catch (err) {
        console.error('Error fetching events:', err);
      } finally {
        setLoadingDates(false);
      }
    };
    fetchEvents();
  }, []);

  // Fetch capacity data for all events
  useEffect(() => {
    const fetchCapacity = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/capacity`);
        if (response.ok) {
          const data = await response.json();
          const capacityMap = {};
          (data.events || []).forEach(event => {
            capacityMap[`${event.venue_id}_${event.date}`] = event;
          });
          setCapacityData(capacityMap);
        }
      } catch (err) {
        console.error('Error fetching capacity:', err);
      }
    };
    fetchCapacity();
    // Refresh every 60 seconds
    const interval = setInterval(fetchCapacity, 60000);
    return () => clearInterval(interval);
  }, []);

  // Helper function to get available seats for a show
  const getAvailableSeats = (show) => {
    const key = `${show.venueId}_${show.date}`;
    const capacity = capacityData[key];
    if (capacity) {
      return capacity.available;
    }
    // Default to venue capacity if no data yet
    const venue = VENUES[show.venueId];
    return venue?.capacity || 200;
  };

  // Blocca scroll quando modal è aperto
  useEffect(() => {
    if (selectedDate) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedDate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateFormChange = (e) => {
    const { name, value } = e.target;
    setDateFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateFormSelectChange = (name, value) => {
    setDateFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTheatreFormChange = (e) => {
    const { name, value } = e.target;
    setTheatreForm(prev => ({ ...prev, [name]: value }));
    // Rimuovi errore quando l'utente inizia a digitare
    if (theatreErrors[name]) {
      setTheatreErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateTheatreForm = () => {
    const errors = {};
    if (!theatreForm.nomeCompleto.trim()) errors.nomeCompleto = 'Campo obbligatorio';
    if (!theatreForm.nomeTeatro.trim()) errors.nomeTeatro = 'Campo obbligatorio';
    if (!theatreForm.citta.trim()) errors.citta = 'Campo obbligatorio';
    if (!theatreForm.email.trim()) errors.email = 'Campo obbligatorio';
    else if (!/\S+@\S+\.\S+/.test(theatreForm.email)) errors.email = 'Email non valida';
    if (!theatreForm.telefono.trim()) errors.telefono = 'Campo obbligatorio';
    return errors;
  };

  const handleTheatreFormSubmit = (e) => {
    e.preventDefault();
    const errors = validateTheatreForm();
    
    if (Object.keys(errors).length > 0) {
      setTheatreErrors(errors);
      return;
    }

    const message = `Ciao,
sono ${theatreForm.nomeCompleto} del teatro ${theatreForm.nomeTeatro} a ${theatreForm.citta}.
Vorrei ricevere informazioni per ospitare Sister Tac.

Email: ${theatreForm.email}
Telefono: ${theatreForm.telefono}${theatreForm.capienza ? `
Capienza sala: ${theatreForm.capienza} posti` : ''}${theatreForm.periodo ? `
Periodo richiesto: ${theatreForm.periodo}` : ''}

Resto in attesa di un vostro riscontro. Grazie.`;

    const whatsappUrl = `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.nome.trim() || !formData.cognome.trim()) {
      alert('Inserisci nome e cognome');
      return;
    }
    const whatsappUrl = generateWhatsAppUrl(formData);
    window.open(whatsappUrl, '_blank');
  };

  const handleDateFormSubmit = (e) => {
    e.preventDefault();
    if (!dateFormData.nome.trim() || !dateFormData.cognome.trim()) {
      alert('Inserisci nome e cognome');
      return;
    }
    const whatsappUrl = generateWhatsAppUrl({
      ...dateFormData,
      data: selectedDate.date
    });
    window.open(whatsappUrl, '_blank');
  };

  // Funzione per pagamento con carta di credito
  const handleCardPayment = async () => {
    if (!dateFormData.nome.trim() || !dateFormData.cognome.trim()) {
      alert('Inserisci nome e cognome');
      return;
    }
    
    if (!dateFormData.email.trim() || !/\S+@\S+\.\S+/.test(dateFormData.email)) {
      alert('Inserisci un indirizzo email valido per ricevere la conferma');
      return;
    }
    
    const totalTickets = parseInt(dateFormData.numBiglietti) + parseInt(dateFormData.numBigliettiRidotti || 0);
    if (totalTickets === 0) {
      alert('Seleziona almeno un biglietto');
      return;
    }
    
    // For venues with seat maps, require seat selection
    const venueId = selectedDate?.venueId || 'teatro-sant-anna';
    const venuesWithSeatMap = ['teatro-sant-anna', 'teatro-torretta'];
    if (venuesWithSeatMap.includes(venueId) && selectedSeats.length !== totalTickets) {
      alert(`Seleziona ${totalTickets} posto/i dalla piantina`);
      return;
    }
    
    setIsProcessingPayment(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          venue_id: venueId,
          date: selectedDate?.date || '',
          num_biglietti_interi: parseInt(dateFormData.numBiglietti),
          num_biglietti_ridotti: parseInt(dateFormData.numBigliettiRidotti || 0),
          nome: dateFormData.nome,
          cognome: dateFormData.cognome,
          email: dateFormData.email,
          telefono: dateFormData.telefono || '',
          origin_url: window.location.origin,
          seats: ['teatro-sant-anna', 'teatro-torretta'].includes(venueId) ? selectedSeats : [],
          discount_code: discountApplied ? discountCode : null
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Errore nella creazione del pagamento');
      }
      
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      console.error('Payment error:', error);
      alert(error.message || 'Si è verificato un errore. Riprova.');
      setIsProcessingPayment(false);
    }
  };

  const scrollToBooking = () => {
    document.getElementById('prossime-date').scrollIntoView({ behavior: 'smooth' });
  };

  const openDateModal = async (show) => {
    setSelectedDate(show);
    setDateFormData({ nome: '', cognome: '', email: '', telefono: '', numBiglietti: '1', numBigliettiRidotti: '0' });
    setSelectedSeats([]);
    setIsProcessingPayment(false);
    setDiscountCode('');
    setDiscountApplied(false);
    
    // Load occupied seats for venues with seat maps
    const venuesWithSeatMap = ['teatro-sant-anna', 'teatro-torretta'];
    if (venuesWithSeatMap.includes(show.venueId)) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/seats/${show.venueId}/${encodeURIComponent(show.date)}`
        );
        if (response.ok) {
          const data = await response.json();
          setOccupiedSeats([...data.occupied_seats, ...data.blocked_seats]);
        }
      } catch (err) {
        console.error('Error fetching seats:', err);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">

      {/* 🔥 HERO SECTION */}
      <section className="relative min-h-screen flex flex-col justify-end pb-8" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={GALLERY_IMAGES[0]?.url || "https://customer-assets.emergentagent.com/job_sister-tac/artifacts/gz11huaf_sara%204.jpg"}
            alt="Sister Tac in scena"
            className="w-full h-full object-cover object-top"
          />
          {/* Overlay scuro elegante */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
        </div>

        {/* Content */}
        <div className="relative z-10 px-6 text-center">
          <p className="text-[#E91E63] text-sm font-bold uppercase tracking-[0.3em] mb-4 animate-fade-in-up">
            Il musical più divertente e irriverente d'Italia
          </p>
          
          <h1 className="font-display text-6xl md:text-8xl text-white mb-3 tracking-wider">
            SISTER TAC
          </h1>
          
          <p className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] via-[#E91E63] to-[#D946EF] text-xl md:text-2xl font-medium mb-6 animate-pulse" style={{textShadow: '0 0 30px rgba(233, 30, 99, 0.5)'}}>
            ✨ Una drag queen in abito da suora ✨
          </p>

          <div className="mb-10 space-y-2">
            <p className="text-[#FFD700] text-xl md:text-2xl font-bold">
              🎭 Oltre 20 repliche sold out
            </p>
            <p className="text-white/80 text-base md:text-lg">
              📰 Citato da TorinoSette e La Stampa
            </p>
          </div>

          {/* CTA Primaria */}
          <Button 
            onClick={scrollToBooking}
            className="w-full max-w-sm py-7 text-xl font-bold bg-[#E91E63] hover:bg-[#C2185B] text-white rounded-full shadow-2xl shadow-pink-500/40 mb-4 transition-transform hover:scale-105 animate-pulse"
            data-testid="hero-buy-btn"
          >
            <Ticket className="w-6 h-6 mr-2" />
            🎟️ PRENOTA ORA
          </Button>

          {/* CTA Secondaria */}
          <button 
            onClick={() => document.getElementById('trailer').scrollIntoView({ behavior: 'smooth' })}
            className="flex items-center justify-center gap-2 text-white/70 hover:text-white transition-colors mx-auto mt-6"
            data-testid="hero-trailer-btn"
          >
            <Play className="w-5 h-5" />
            <span>Guarda trailer</span>
          </button>
        </div>
      </section>

      {/* 🔥 SEZIONE PROSSIME DATE - Subito dopo hero per conversione */}
      <section id="prossime-date" className="py-16 px-6 bg-[#0a0a0a]" data-testid="prossime-date-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Prossime Date
          </h2>
          <p className="text-center text-xl md:text-2xl font-medium text-[#FFD700] mb-10">
            👇 Clicca sulla data che preferisci per prenotare
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {loadingDates ? (
              <div className="col-span-full text-center py-8">
                <div className="w-8 h-8 border-4 border-[#E91E63] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-white/60">Caricamento date...</p>
              </div>
            ) : showDates.length === 0 ? (
              <div className="col-span-full text-center py-8">
                <p className="text-white/60">Nessuna data disponibile al momento.</p>
              </div>
            ) : (
              showDates.map((show, index) => {
                // Trova l'indice della prima data disponibile (non sold out)
                const firstAvailableIndex = showDates.findIndex(s => s.available);
                const isNextShow = index === firstAvailableIndex && show.available;
                
                return (
              <button
                key={index}
                onClick={() => show.available && openDateModal(show)}
                disabled={!show.available}
                className={`relative overflow-hidden bg-gradient-to-br from-[#1a1a1a] to-[#121212] p-6 rounded-2xl border transition-all duration-300 text-left group ${
                  isNextShow
                    ? 'border-[#E91E63] ring-2 ring-[#E91E63]/30 hover:border-[#E91E63] hover:scale-[1.02] hover:shadow-lg hover:shadow-pink-500/20 cursor-pointer'
                    : show.available 
                      ? 'border-white/10 hover:border-[#E91E63]/50 hover:scale-[1.02] hover:shadow-lg hover:shadow-pink-500/10 cursor-pointer' 
                      : 'border-white/5 opacity-70 cursor-not-allowed'
                }`}
                data-testid={`date-card-hero-${index}`}
              >
                {/* Badge PROSSIMO SPETTACOLO per la prima data disponibile */}
                {isNextShow && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-[#E91E63] to-[#C2185B] text-white text-xs font-bold py-1.5 px-4 text-center">
                    PROSSIMO SPETTACOLO
                  </div>
                )}
                {/* Striscia SOLD OUT */}
                {!show.available && (
                  <div className="absolute top-4 -right-8 bg-red-600 text-white text-xs font-bold py-1 px-10 rotate-45 shadow-lg z-10">
                    SOLD OUT
                  </div>
                )}
                
                <div className={`flex items-start justify-between mb-4 ${isNextShow ? 'mt-6' : ''}`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${show.available ? 'bg-[#E91E63]/20' : 'bg-white/10'}`}>
                    <Calendar className={`w-6 h-6 ${show.available ? 'text-[#E91E63]' : 'text-white/40'}`} />
                  </div>
                  {/* Prezzi - mostra intero/ridotto se disponibili */}
                  {show.prices ? (
                    <div className="text-right">
                      <span className={`font-bold text-lg ${show.available ? 'text-[#FFD700]' : 'text-white/40 line-through'}`}>{show.prices.intero}€</span>
                      <p className="text-white/50 text-xs">Ridotto {show.prices.ridotto}€</p>
                    </div>
                  ) : (
                    <span className={`font-bold text-xl ${show.available ? 'text-[#FFD700]' : 'text-white/40 line-through'}`}>{SHOW_INFO.ticketPrice}€</span>
                  )}
                </div>
                
                {/* Data con città */}
                {(() => {
                  const venue = getVenueForDate(show);
                  return (
                    <p className={`font-bold text-xl md:text-2xl mb-1 ${show.available ? 'text-white' : 'text-white/50'}`}>
                      {show.date} <span className="text-[#E91E63]">{venue.city}</span>
                    </p>
                  );
                })()}
                <p className="text-white/70 text-sm md:text-base mb-2">{show.day}</p>
                
                {/* Indirizzo teatro con link info - più leggibile */}
                {(() => {
                  const venue = getVenueForDate(show);
                  return (
                    <div className="flex items-start gap-2 mb-3 p-2 bg-white/5 rounded-lg">
                      <MapPin className="w-4 h-4 text-[#E91E63] flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm md:text-base">{venue.name}</p>
                        <p className="text-white/60 text-xs md:text-sm">{venue.address}</p>
                      </div>
                      <Link 
                        to={`/info#${show.venueId || 'teatro-sant-anna'}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[#FFD700] hover:text-[#E91E63] flex-shrink-0 p-1"
                        title="Info su questa location"
                      >
                        <Info className="w-4 h-4" />
                      </Link>
                    </div>
                  );
                })()}
                
                <div className="flex items-center gap-4 text-white/60 text-sm md:text-base mb-4">
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4 md:w-5 md:h-5" />
                    Ore {show.time}
                  </span>
                </div>
                
                {/* Barra di progresso posti venduti - Solo per teatri con pagamento carta */}
                {show.available && show.venueId !== 'teatro-incontro' && (
                  <div className="mb-4">
                    {(() => {
                      const available = getAvailableSeats(show);
                      const venue = VENUES[show.venueId];
                      const maxCapacity = venue?.capacity || 200;
                      const sold = maxCapacity - available;
                      const percentSold = (sold / maxCapacity) * 100;
                      
                      // Colore della barra in base al riempimento
                      let barColor = 'bg-[#25D366]'; // Verde per < 50%
                      let textColor = 'text-white/70';
                      if (percentSold >= 80) {
                        barColor = 'bg-red-500';
                        textColor = 'text-red-400';
                      } else if (percentSold >= 50) {
                        barColor = 'bg-orange-500';
                        textColor = 'text-orange-400';
                      }
                      
                      if (available <= 0) {
                        return (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                              <div className="h-full bg-red-500 rounded-full" style={{ width: '100%' }}></div>
                            </div>
                            <span className="text-red-500 text-xs font-bold whitespace-nowrap">SOLD OUT</span>
                          </div>
                        );
                      }
                      
                      return (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div className={`h-full ${barColor} rounded-full transition-all duration-500`} style={{ width: `${percentSold}%` }}></div>
                          </div>
                          <span className={`${textColor} text-xs font-medium whitespace-nowrap`}>
                            {sold}/{maxCapacity} venduti
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  {show.available ? (
                    <>
                      <span className="text-[#25D366] text-xs md:text-sm bg-[#25D366]/10 px-3 py-1.5 rounded-full font-medium">
                        Disponibile
                      </span>
                      <span className="text-[#E91E63] text-sm md:text-base font-bold group-hover:translate-x-1 transition-transform flex items-center gap-1">
                        Prenota <ChevronRight className="w-5 h-5" />
                      </span>
                    </>
                  ) : (
                    <span className="text-red-500 text-xs md:text-sm bg-red-500/10 px-3 py-1.5 rounded-full font-medium">
                      Esaurito
                    </span>
                  )}
                </div>
              </button>
            );
            })
            )}
          </div>
          
          <p className="text-center text-white/40 text-sm mt-6">
            Clicca su una data per prenotare
          </p>
        </div>
      </section>

      {/* 🔥 SEZIONE 2 – ENERGIA */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#0a0a0a] to-[#121212]" data-testid="energia-section">
        <div className="max-w-lg mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-10">
            Non è il solito musical.
          </h2>
          
          <div className="space-y-4 text-xl text-white/80 mb-12">
            <p>È <span className="text-[#E91E63] font-bold">energia pura</span>.</p>
            <p>È <span className="text-[#FFD700] font-bold">ironia senza filtri</span>.</p>
            <p>È la serata che <span className="text-white font-bold">racconti il giorno dopo</span>.</p>
          </div>

          <Button 
            onClick={scrollToBooking}
            className="py-6 px-10 text-lg font-bold bg-[#E91E63] hover:bg-[#C2185B] text-white rounded-full"
            data-testid="energia-buy-btn"
          >
            <Ticket className="w-5 h-5 mr-2" />
            Scegli la tua data
          </Button>
        </div>
      </section>

      {/* 🔥 SEZIONE TRAMA */}
      <section id="trama" className="py-20 px-6 bg-[#0a0a0a]" data-testid="trama-section">
        <div className="max-w-lg mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-8">
            Di cosa parla?
          </h2>
          
          <div className="space-y-6 text-white/80 text-lg leading-relaxed">
            <p>
              Una <span className="text-[#E91E63] font-semibold">drag queen talentuosa</span>, 
              abituata a brillare sotto i riflettori dei locali più glamour della città.
            </p>
            <p>
              Una notte, tutto cambia: diventa testimone involontaria di un omicidio.
            </p>
            <p>
              Per sfuggire ai criminali, l'unica via di fuga è nascondersi nel posto 
              più improbabile: <span className="text-[#FFD700] font-semibold">un convento di suore</span>.
            </p>
            <p>
              Tra una Madre Superiora teneramente intransigente, un Padre Pisapia 
              folle quanto basta e le sorelle più scatenate della città...
            </p>
            <p className="text-white text-xl font-medium pt-4 text-center">
              <span className="text-[#E91E63]">Paillettes sotto il velo?</span> Forse sì.
            </p>
          </div>

          <div className="text-center mt-10">
            <Button 
              onClick={scrollToBooking}
              className="py-6 px-10 text-lg font-bold bg-[#E91E63] hover:bg-[#C2185B] text-white rounded-full"
              data-testid="trama-buy-btn"
            >
              <Ticket className="w-5 h-5 mr-2" />
              Voglio vederlo!
            </Button>
          </div>
        </div>
      </section>

      {/* 🔥 SEZIONE 3 – PERCHÉ VENIRE */}
      <section className="py-20 px-6 bg-[#0a0a0a]" data-testid="perche-section">
        <div className="max-w-lg mx-auto">
          <div className="grid grid-cols-1 gap-8">
            {/* Blocco 1 */}
            <div className="text-center p-8 rounded-3xl bg-gradient-to-br from-[#1a1a1a] to-[#121212] border border-white/5">
              <div className="text-5xl mb-4">🤣</div>
              <h3 className="text-2xl font-bold text-white mb-2">Ridi davvero</h3>
              <p className="text-white/60">Due ore di risate genuine, non forzate.</p>
            </div>

            {/* Blocco 2 */}
            <div className="text-center p-8 rounded-3xl bg-gradient-to-br from-[#1a1a1a] to-[#121212] border border-white/5">
              <div className="text-5xl mb-4">🎵</div>
              <h3 className="text-2xl font-bold text-white mb-2">Canti con noi</h3>
              <p className="text-white/60">Musica travolgente che ti resta addosso.</p>
            </div>

            {/* Blocco 3 */}
            <div className="text-center p-8 rounded-3xl bg-gradient-to-br from-[#1a1a1a] to-[#121212] border border-white/5">
              <div className="text-5xl mb-4">💜</div>
              <h3 className="text-2xl font-bold text-white mb-2">Esci con qualcosa in più</h3>
              <p className="text-white/60">Un messaggio che ti resta nel cuore.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 🔥 SEZIONE 4 – TRAILER / FOTO */}
      <section id="trailer" className="py-20 px-6 bg-gradient-to-b from-[#0a0a0a] to-[#121212]" data-testid="trailer-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-10">
            Guarda cosa succede sul palco.
          </h2>

          {/* Video */}
          <div className="relative aspect-video rounded-2xl overflow-hidden border border-white/10 mb-8 shadow-2xl shadow-pink-500/10">
            <video
              className="w-full h-full object-cover"
              controls
              poster={GALLERY_IMAGES[0]?.url}
              data-testid="promo-video"
            >
              <source src={VIDEO_PROMO} type="video/mp4" />
            </video>
          </div>

          {/* Gallery */}
          <div className="grid grid-cols-2 gap-4 mb-10">
            {GALLERY_IMAGES.slice(0, 4).map((image, index) => (
              <div 
                key={index} 
                className="aspect-square rounded-xl overflow-hidden border border-white/10"
                data-testid={`gallery-img-${index}`}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button 
              onClick={scrollToBooking}
              className="py-6 px-10 text-lg font-bold bg-[#E91E63] hover:bg-[#C2185B] text-white rounded-full"
              data-testid="trailer-buy-btn"
            >
              <Ticket className="w-5 h-5 mr-2" />
              Prenota ora
            </Button>
          </div>
        </div>
      </section>

      {/* Sezione prossime date duplicata rimossa - ora usa solo quella dopo l'hero */}

      {/* 🔥 SEZIONE 5 – PROVA SOCIALE */}
      <section className="py-20 px-6 bg-[#0a0a0a]" data-testid="social-proof-section">
        <div className="max-w-lg mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Chi viene, torna.
          </h2>
          
          {/* Badge */}
          <div className="flex justify-center mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#E91E63]/20 border border-[#E91E63]/30 rounded-full text-[#E91E63] text-sm font-medium">
              <Star className="w-4 h-4 fill-current" />
              Evento molto richiesto
            </span>
          </div>

          {/* Testimonianze */}
          <div className="space-y-6">
            <div className="bg-[#121212] p-6 rounded-2xl border-l-4 border-[#FFD700]">
              <p className="text-white text-lg italic mb-3">"Non ridevo così da anni."</p>
              <p className="text-white/50 text-sm">— Maria G.</p>
            </div>
            
            <div className="bg-[#121212] p-6 rounded-2xl border-l-4 border-[#E91E63]">
              <p className="text-white text-lg italic mb-3">"Due ore volate. Incredibile."</p>
              <p className="text-white/50 text-sm">— Luca P.</p>
            </div>
            
            <div className="bg-[#121212] p-6 rounded-2xl border-l-4 border-[#FFD700]">
              <p className="text-white text-lg italic mb-3">"Energia pazzesca sul palco!"</p>
              <p className="text-white/50 text-sm">— Francesca R.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SEZIONE PRENOTAZIONE NASCOSTA - prenotazione tramite modal su Prossime Date */}

      {/* 🔥 SEZIONE TEATRI E GESTORI */}
      <section id="teatri" className="py-20 px-6 bg-gradient-to-b from-[#1a1512] to-[#1f1a15]" data-testid="theatres-section">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <span className="text-4xl mb-4 block">🎭</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Porta Sister Tac nel tuo teatro
            </h2>
            <p className="text-[#FFD700] text-xl font-medium mb-6">
              Vuoi Sister Tac nella tua città?
            </p>
            <p className="text-white/70 leading-relaxed">
              Sei il proprietario o il gestore di un teatro, di una sala spettacoli o di uno spazio eventi?
              <br className="hidden md:block" />
              Sister Tac è un musical energico, divertente e coinvolgente, pronto a salire sul palco anche nella tua città.
            </p>
            <p className="text-white/50 text-sm mt-4">
              Compila il modulo qui sotto per ricevere informazioni su disponibilità, scheda tecnica e proposta economica.
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 md:p-8 border border-white/10 shadow-xl">
            <form onSubmit={handleTheatreFormSubmit} className="space-y-5" data-testid="theatre-form">
              
              {/* Nome e Cognome */}
              <div className="space-y-2">
                <Label htmlFor="theatre-nome" className="text-white/80">Nome e Cognome *</Label>
                <Input
                  id="theatre-nome"
                  name="nomeCompleto"
                  value={theatreForm.nomeCompleto}
                  onChange={handleTheatreFormChange}
                  placeholder="Il tuo nome completo"
                  className={`bg-[#0a0a0a] border-white/20 text-white h-12 rounded-xl focus:border-[#E91E63] w-full ${theatreErrors.nomeCompleto ? 'border-red-500' : ''}`}
                  data-testid="theatre-input-nome"
                />
                {theatreErrors.nomeCompleto && <p className="text-red-400 text-sm">{theatreErrors.nomeCompleto}</p>}
              </div>

              {/* Nome Teatro */}
              <div className="space-y-2">
                <Label htmlFor="theatre-struttura" className="text-white/80">Nome del Teatro / Struttura *</Label>
                <Input
                  id="theatre-struttura"
                  name="nomeTeatro"
                  value={theatreForm.nomeTeatro}
                  onChange={handleTheatreFormChange}
                  placeholder="Es. Teatro Comunale, Auditorium..."
                  className={`bg-[#0a0a0a] border-white/20 text-white h-12 rounded-xl focus:border-[#E91E63] w-full ${theatreErrors.nomeTeatro ? 'border-red-500' : ''}`}
                  data-testid="theatre-input-struttura"
                />
                {theatreErrors.nomeTeatro && <p className="text-red-400 text-sm">{theatreErrors.nomeTeatro}</p>}
              </div>

              {/* Città */}
              <div className="space-y-2">
                <Label htmlFor="theatre-citta" className="text-white/80">Città / Provincia *</Label>
                <Input
                  id="theatre-citta"
                  name="citta"
                  value={theatreForm.citta}
                  onChange={handleTheatreFormChange}
                  placeholder="Es. Milano (MI)"
                  className={`bg-[#0a0a0a] border-white/20 text-white h-12 rounded-xl focus:border-[#E91E63] w-full ${theatreErrors.citta ? 'border-red-500' : ''}`}
                  data-testid="theatre-input-citta"
                />
                {theatreErrors.citta && <p className="text-red-400 text-sm">{theatreErrors.citta}</p>}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="theatre-email" className="text-white/80">Email professionale *</Label>
                <Input
                  id="theatre-email"
                  name="email"
                  type="email"
                  value={theatreForm.email}
                  onChange={handleTheatreFormChange}
                  placeholder="email@teatro.it"
                  className={`bg-[#0a0a0a] border-white/20 text-white h-12 rounded-xl focus:border-[#E91E63] w-full ${theatreErrors.email ? 'border-red-500' : ''}`}
                  data-testid="theatre-input-email"
                />
                {theatreErrors.email && <p className="text-red-400 text-sm">{theatreErrors.email}</p>}
              </div>

              {/* Telefono */}
              <div className="space-y-2">
                <Label htmlFor="theatre-telefono" className="text-white/80">Telefono *</Label>
                <Input
                  id="theatre-telefono"
                  name="telefono"
                  type="tel"
                  value={theatreForm.telefono}
                  onChange={handleTheatreFormChange}
                  placeholder="+39 123 456 7890"
                  className={`bg-[#0a0a0a] border-white/20 text-white h-12 rounded-xl focus:border-[#E91E63] w-full ${theatreErrors.telefono ? 'border-red-500' : ''}`}
                  data-testid="theatre-input-telefono"
                />
                {theatreErrors.telefono && <p className="text-red-400 text-sm">{theatreErrors.telefono}</p>}
              </div>

              {/* Campi facoltativi */}
              <div className="pt-4 border-t border-white/10">
                <p className="text-white/40 text-sm mb-4">Campi facoltativi</p>
                
                {/* Capienza */}
                <div className="space-y-2 mb-4">
                  <Label htmlFor="theatre-capienza" className="text-white/80">Capienza sala (numero posti)</Label>
                  <Input
                    id="theatre-capienza"
                    name="capienza"
                    type="number"
                    value={theatreForm.capienza}
                    onChange={handleTheatreFormChange}
                    placeholder="Es. 350"
                    className="bg-[#0a0a0a] border-white/20 text-white h-12 rounded-xl focus:border-[#E91E63] w-full"
                    data-testid="theatre-input-capienza"
                  />
                </div>

                {/* Periodo */}
                <div className="space-y-2">
                  <Label htmlFor="theatre-periodo" className="text-white/80">Periodo o date di interesse</Label>
                  <Input
                    id="theatre-periodo"
                    name="periodo"
                    value={theatreForm.periodo}
                    onChange={handleTheatreFormChange}
                    placeholder="Es. Marzo 2026, Weekend di Aprile..."
                    className="bg-[#0a0a0a] border-white/20 text-white h-12 rounded-xl focus:border-[#E91E63] w-full"
                    data-testid="theatre-input-periodo"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit"
                className="w-full py-6 text-lg font-bold bg-[#E91E63] hover:bg-[#C2185B] text-white rounded-full mt-6 min-h-[48px]"
                data-testid="theatre-submit-btn"
              >
                <Send className="w-5 h-5 mr-2" />
                Invia richiesta
              </Button>

              {/* Nota finale */}
              <p className="text-center text-white/50 text-sm flex items-center justify-center gap-2">
                <MapPin className="w-4 h-4" />
                Ti ricontatteremo entro 48 ore lavorative con una proposta personalizzata.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* 🔥 SEZIONE FINALE */}
      <section className="py-24 px-6 bg-[#0a0a0a] border-t border-white/5" data-testid="final-section">
        <div className="max-w-lg mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Non rimandare.
          </h2>
          
          <p className="text-white/60 text-xl mb-10">
            Le serate migliori non si improvvisano.
          </p>

          <Button 
            onClick={scrollToBooking}
            className="w-full max-w-sm py-8 text-xl font-bold bg-[#E91E63] hover:bg-[#C2185B] text-white rounded-full shadow-2xl shadow-pink-500/40 transition-transform hover:scale-105"
            data-testid="final-buy-btn"
          >
            <Ticket className="w-6 h-6 mr-2" />
            PRENDI IL TUO POSTO ADESSO
          </Button>
        </div>
      </section>

      {/* Spacer per sticky CTA */}
      <div className="h-24 md:hidden" />

      {/* MODAL PRENOTAZIONE DATA */}
      {selectedDate && (
        <div 
          className="fixed inset-0 z-[200] flex items-center justify-center p-2 md:p-4"
          data-testid="date-modal"
        >
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedDate(null)}
          />
          
          {/* Modal Content - wider for seat map */}
          <div className={`relative bg-[#121212] rounded-3xl border border-white/10 w-full ${['teatro-sant-anna', 'teatro-torretta'].includes(selectedDate?.venueId) ? 'max-w-3xl' : 'max-w-md'} max-h-[95vh] overflow-y-auto`}>
            {/* Header */}
            <div className="sticky top-0 bg-[#121212] p-4 md:p-6 border-b border-white/10 flex items-start justify-between rounded-t-3xl z-10">
              <div>
                <p className="text-[#E91E63] text-sm font-medium mb-1">Prenotazione per</p>
                <h3 className="text-white text-xl md:text-2xl font-bold">{selectedDate.date}</h3>
                <p className="text-white/60 text-sm">{selectedDate.day} • ore {selectedDate.time}</p>
              </div>
              <button
                onClick={() => setSelectedDate(null)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                aria-label="Chiudi"
              >
                <X className="w-6 h-6 text-white/60" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleDateFormSubmit} className="p-6 space-y-5">
              <div className="flex items-center gap-3 p-4 bg-[#E91E63]/10 rounded-xl border border-[#E91E63]/20">
                <MapPin className="w-5 h-5 text-[#E91E63]" />
                <div>
                  {(() => {
                    const venue = selectedDate ? getVenueForDate(selectedDate) : SHOW_INFO.venue;
                    return (
                      <>
                        <p className="text-white text-sm font-medium">{venue.name}</p>
                        <p className="text-white/60 text-xs">{venue.address}, {venue.city}</p>
                      </>
                    );
                  })()}
                </div>
              </div>
              
              {/* Nota speciale per l'evento se presente */}
              {selectedDate?.note && (
                <div className="p-3 bg-[#FFD700]/10 rounded-xl border border-[#FFD700]/20">
                  <p className="text-[#FFD700] text-xs">{selectedDate.note}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="modal-nome" className="text-white/80">Nome *</Label>
                <Input
                  id="modal-nome"
                  name="nome"
                  value={dateFormData.nome}
                  onChange={handleDateFormChange}
                  placeholder="Il tuo nome"
                  className="bg-[#0a0a0a] border-white/20 text-white h-12 rounded-xl focus:border-[#E91E63]"
                  required
                  data-testid="modal-input-nome"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="modal-cognome" className="text-white/80">Cognome *</Label>
                <Input
                  id="modal-cognome"
                  name="cognome"
                  value={dateFormData.cognome}
                  onChange={handleDateFormChange}
                  placeholder="Il tuo cognome"
                  className="bg-[#0a0a0a] border-white/20 text-white h-12 rounded-xl focus:border-[#E91E63]"
                  required
                  data-testid="modal-input-cognome"
                />
              </div>

              {/* Email - richiesta per pagamento con carta */}
              {(() => {
                const venueId = selectedDate?.venueId || 'teatro-sant-anna';
                const cardOnlyVenues = ['teatro-sant-anna', 'teatro-torretta'];
                const requiresEmail = cardOnlyVenues.includes(venueId);
                return requiresEmail && (
                  <div className="space-y-2">
                    <Label htmlFor="modal-email" className="text-white/80">Email * <span className="text-white/50 text-xs">(per conferma prenotazione)</span></Label>
                    <Input
                      id="modal-email"
                      name="email"
                      type="email"
                      value={dateFormData.email}
                      onChange={handleDateFormChange}
                      placeholder="tuaemail@esempio.com"
                      className="bg-[#0a0a0a] border-white/20 text-white h-12 rounded-xl focus:border-[#E91E63]"
                      required
                      data-testid="modal-input-email"
                    />
                  </div>
                );
              })()}

              <div className="space-y-2">
                <Label htmlFor="modal-telefono" className="text-white/80">Telefono</Label>
                <Input
                  id="modal-telefono"
                  name="telefono"
                  type="tel"
                  value={dateFormData.telefono}
                  onChange={handleDateFormChange}
                  placeholder="+39 123 456 7890"
                  className="bg-[#0a0a0a] border-white/20 text-white h-12 rounded-xl focus:border-[#E91E63]"
                  data-testid="modal-input-telefono"
                />
              </div>

              {/* Selezione biglietti - diversa se ci sono prezzi specifici */}
              {selectedDate?.prices ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="modal-biglietti-interi" className="text-white/80">Biglietti Interi ({selectedDate.prices.intero}€) *</Label>
                    <Select 
                      value={dateFormData.numBiglietti} 
                      onValueChange={(value) => handleDateFormSelectChange('numBiglietti', value)}
                    >
                      <SelectTrigger className="bg-[#0a0a0a] border-white/20 text-white h-12 rounded-xl" data-testid="modal-select-biglietti">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10 z-[300]">
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                          <SelectItem 
                            key={num} 
                            value={num.toString()}
                            className="text-white hover:bg-white/10"
                          >
                            {num}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="modal-biglietti-ridotti" className="text-white/80">{selectedDate.prices.ridottoLabel} ({selectedDate.prices.ridotto}€)</Label>
                    <Select 
                      value={dateFormData.numBigliettiRidotti} 
                      onValueChange={(value) => handleDateFormSelectChange('numBigliettiRidotti', value)}
                    >
                      <SelectTrigger className="bg-[#0a0a0a] border-white/20 text-white h-12 rounded-xl" data-testid="modal-select-biglietti-ridotti">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10 z-[300]">
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                          <SelectItem 
                            key={num} 
                            value={num.toString()}
                            className="text-white hover:bg-white/10"
                          >
                            {num}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="modal-biglietti" className="text-white/80">Numero biglietti *</Label>
                  <Select 
                    value={dateFormData.numBiglietti} 
                    onValueChange={(value) => handleDateFormSelectChange('numBiglietti', value)}
                  >
                    <SelectTrigger className="bg-[#0a0a0a] border-white/20 text-white h-12 rounded-xl" data-testid="modal-select-biglietti">
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10 z-[300]">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                        <SelectItem 
                          key={num} 
                          value={num.toString()}
                          className="text-white hover:bg-white/10"
                        >
                          {num} {num === 1 ? 'biglietto' : 'biglietti'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Seat Map - Per teatri con piantina */}
              {['teatro-sant-anna', 'teatro-torretta'].includes(selectedDate?.venueId) && (
                <div className="pt-4 border-t border-white/10">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#E91E63]" />
                    Scegli i tuoi posti
                  </h3>
                  <p className="text-white/50 text-sm mb-4">
                    Seleziona {parseInt(dateFormData.numBiglietti) + parseInt(dateFormData.numBigliettiRidotti || 0)} posto/i dalla piantina
                  </p>
                  <SeatMap
                    date={selectedDate?.date}
                    venueId={selectedDate?.venueId}
                    occupiedSeats={occupiedSeats}
                    selectedSeats={selectedSeats}
                    onSeatSelect={setSelectedSeats}
                    maxSeats={parseInt(dateFormData.numBiglietti) + parseInt(dateFormData.numBigliettiRidotti || 0)}
                  />
                </div>
              )}

              {/* Codice Sconto - Solo per teatri senza prezzi differenziati (Sant'Anna) */}
              {!selectedDate?.prices && (
                <div className="pt-4 border-t border-white/10">
                  <Label htmlFor="discount-code" className="text-white/80 text-sm">Hai un codice sconto?</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="discount-code"
                      type="text"
                      value={discountCode}
                      onChange={(e) => {
                        setDiscountCode(e.target.value.toLowerCase());
                        setDiscountApplied(false);
                      }}
                      placeholder="Inserisci codice"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/30 flex-1"
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        const code = discountCode.toLowerCase();
                        const currentDate = selectedDate?.date || '';
                        
                        // Codice "bordieri" valido SOLO per 24 Maggio
                        if (code === 'bordieri') {
                          if (currentDate.includes('24 Maggio')) {
                            setDiscountApplied(true);
                          } else {
                            setDiscountApplied(false);
                            alert('Il codice "bordieri" è valido solo per la data del 24 Maggio');
                          }
                        }
                        // Altri codici sconto (validi per tutte le date)
                        else if (code === 'passaparola' || code === 'nudepower' || code === 'contatto') {
                          setDiscountApplied(true);
                        } else {
                          setDiscountApplied(false);
                          alert('Codice sconto non valido');
                        }
                      }}
                      className="bg-[#E91E63]/20 hover:bg-[#E91E63]/40 text-[#E91E63]"
                    >
                      Applica
                    </Button>
                  </div>
                  {discountApplied && (
                    <p className="text-[#25D366] text-sm mt-2 flex items-center gap-1">
                      ✓ Codice "{discountCode}" applicato! Sconto 1€ per biglietto
                    </p>
                  )}
                </div>
              )}

              {/* Prezzo */}
              <div className="pt-4 border-t border-white/10">
                <div className="flex justify-between items-center">
                  <span className="text-white/60">Totale</span>
                  <div className="text-right">
                    {discountApplied && !selectedDate?.prices && (
                      <span className="text-white/40 line-through text-lg mr-2">
                        {parseInt(dateFormData.numBiglietti) * SHOW_INFO.ticketPrice}€
                      </span>
                    )}
                    <span className="text-[#FFD700] text-2xl font-bold">
                      {selectedDate?.prices 
                        ? (parseInt(dateFormData.numBiglietti) * selectedDate.prices.intero) + (parseInt(dateFormData.numBigliettiRidotti || 0) * selectedDate.prices.ridotto)
                        : parseInt(dateFormData.numBiglietti) * (discountApplied ? (discountCode.toLowerCase() === 'bordieri' ? 10 : 12) : SHOW_INFO.ticketPrice)
                      }€
                    </span>
                  </div>
                </div>
                {selectedDate?.prices && (parseInt(dateFormData.numBiglietti) > 0 || parseInt(dateFormData.numBigliettiRidotti) > 0) && (
                  <p className="text-white/40 text-xs mt-1">
                    {parseInt(dateFormData.numBiglietti) > 0 && `${dateFormData.numBiglietti} intero/i`}
                    {parseInt(dateFormData.numBiglietti) > 0 && parseInt(dateFormData.numBigliettiRidotti) > 0 && ' + '}
                    {parseInt(dateFormData.numBigliettiRidotti) > 0 && `${dateFormData.numBigliettiRidotti} ridotto/i`}
                  </p>
                )}
                {discountApplied && !selectedDate?.prices && (
                  <p className="text-[#25D366] text-xs mt-1">
                    {dateFormData.numBiglietti} biglietto/i × {discountCode.toLowerCase() === 'bordieri' ? '10' : '12'}€ (sconto {discountCode})
                  </p>
                )}
              </div>

              {/* Bottoni di pagamento */}
              {(() => {
                const venue = selectedDate ? getVenueForDate(selectedDate) : SHOW_INFO.venue;
                const phone = selectedDate?.phone ? '39' + selectedDate.phone : CONTACT_INFO.whatsapp;
                const phoneDisplay = selectedDate?.phone ? '+39 ' + selectedDate.phone : CONTACT_INFO.whatsappDisplay;
                const venueId = selectedDate?.venueId || 'teatro-sant-anna';
                
                // Teatri con SOLO pagamento carta (no WhatsApp)
                const cardOnlyVenues = ['teatro-sant-anna', 'teatro-torretta'];
                const canPayWithCard = cardOnlyVenues.includes(venueId);
                const showWhatsApp = !cardOnlyVenues.includes(venueId);
                
                const isFormValid = dateFormData.nome.trim() && dateFormData.cognome.trim() && (parseInt(dateFormData.numBiglietti) > 0 || parseInt(dateFormData.numBigliettiRidotti || 0) > 0);
                const isFormValidForCard = isFormValid && dateFormData.email.trim() && /\S+@\S+\.\S+/.test(dateFormData.email);
                
                return (
                  <>
                    {/* Pagamento con carta - Per Teatro Sant'Anna e Teatro Torretta */}
                    {canPayWithCard && (
                      <>
                        <button
                          onClick={handleCardPayment}
                          disabled={!isFormValidForCard || isProcessingPayment}
                          className={`w-full py-6 text-lg font-bold bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] hover:from-[#4F46E5] hover:to-[#7C3AED] text-white rounded-full flex items-center justify-center gap-2 transition-all ${(!isFormValidForCard || isProcessingPayment) ? 'opacity-50 cursor-not-allowed' : ''}`}
                          data-testid="modal-card-payment-btn"
                        >
                          {isProcessingPayment ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Elaborazione...
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-5 h-5" />
                              PAGA CON CARTA
                            </>
                          )}
                        </button>
                        
                        {/* Info contatto per assistenza */}
                        <p className="text-center text-white/50 text-sm mt-3">
                          Per assistenza: <a href={`tel:${phoneDisplay.replace(/\s/g, '')}`} className="text-[#E91E63] font-medium underline">{phoneDisplay}</a>
                        </p>
                      </>
                    )}
                    
                    {/* Prenota su WhatsApp - Solo per teatri senza pagamento carta */}
                    {showWhatsApp && (() => {
                      const whatsappMessage = encodeURIComponent(`Ciao! Vorrei prenotare per SISTER TAC. Nome: ${dateFormData.nome} ${dateFormData.cognome}. Telefono: ${dateFormData.telefono || 'non indicato'}. ${selectedDate?.prices ? `Biglietti interi: ${dateFormData.numBiglietti}. Biglietti ridotti: ${dateFormData.numBigliettiRidotti}.` : `Biglietti: ${dateFormData.numBiglietti}.`} Data: ${selectedDate?.date || 'Da definire'}. Location: ${getVenueForDate(selectedDate).name}, ${getVenueForDate(selectedDate).city}.`);
                      const whatsappUrl = `https://wa.me/${phone}?text=${whatsappMessage}`;
                      
                      // Function to save request AND open WhatsApp
                      const handleWhatsAppClick = (e) => {
                        e.preventDefault();
                        
                        // Prevent double clicks
                        if (!isFormValid || isSubmittingWhatsApp) {
                          return;
                        }
                        
                        setIsSubmittingWhatsApp(true);
                        
                        // FIRST: Open WhatsApp IMMEDIATELY (must be synchronous for browser to allow)
                        window.open(whatsappUrl, '_blank');
                        
                        // THEN: Save the request to database in background (don't await)
                        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp-requests`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            venue_id: selectedDate?.venueId || 'teatro-incontro',
                            date: selectedDate?.date || '',
                            nome: dateFormData.nome,
                            cognome: dateFormData.cognome,
                            telefono: dateFormData.telefono || '',
                            email: '',
                            num_biglietti_interi: parseInt(dateFormData.numBiglietti) || 1,
                            num_biglietti_ridotti: parseInt(dateFormData.numBigliettiRidotti) || 0,
                            posti_richiesti: '',
                            note: ''
                          })
                        }).then(() => {
                          console.log('WhatsApp request saved successfully');
                        }).catch(err => {
                          console.error('Error saving WhatsApp request:', err);
                        });
                        
                        // Reset after a delay to allow new submissions later
                        setTimeout(() => setIsSubmittingWhatsApp(false), 3000);
                      };
                      
                      return (
                        <>
                          <button
                            onClick={handleWhatsAppClick}
                            disabled={!isFormValid || isSubmittingWhatsApp}
                            className={`w-full py-6 text-lg font-bold bg-[#E91E63] hover:bg-[#C2185B] text-white rounded-full flex items-center justify-center gap-2 ${(!isFormValid || isSubmittingWhatsApp) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            data-testid="modal-submit-btn"
                          >
                            <Send className="w-5 h-5" />
                            {isSubmittingWhatsApp ? 'INVIO IN CORSO...' : 'PRENOTA SU WHATSAPP'}
                          </button>
                          
                          {/* Numero alternativo se il link non funziona */}
                          <p className="text-center text-white/50 text-sm mt-3">
                            Problemi con WhatsApp? Scrivi o chiama direttamente:<br/>
                            <a href={`tel:${phoneDisplay.replace(/\s/g, '')}`} className="text-[#25D366] font-medium underline">{phoneDisplay}</a>
                          </p>
                        </>
                      );
                    })()}
                  </>
                );
              })()}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
